import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/app/model/store';

const selectAuth = (state: RootState) => state.auth.isAuthenticated;

export const selectIsAuth = createSelector(selectAuth, (isAuth) => isAuth);

export const selectMyPermissions = (state: RootState) =>
  state.auth.myPermissions;

export const selectMyPermissionsLoading = (state: RootState) =>
  state.auth.myPermissionsLoading;

export const selectHasAdminBasicAccess = createSelector(
  selectMyPermissions,
  (myPermissions) => myPermissions?.BasicAccess ?? false,
);

/**
 * Distributor admins see a reduced organization page. Permissions arrive
 * asynchronously, so wait for them instead of treating the initial `null`
 * as "no basic access" — otherwise a full admin briefly gets the reduced view.
 * A failed request keeps the reduced view: showing the full admin page to
 * someone whose access could not be confirmed is the worse mistake.
 */
export const selectIsDistributorView = createSelector(
  selectMyPermissionsLoading,
  selectHasAdminBasicAccess,
  (loading, hasAdminBasicAccess) =>
    (loading === 'succeeded' || loading === 'failed') && !hasAdminBasicAccess,
);

export const selectCanViewSystemLoad = createSelector(
  selectMyPermissions,
  (myPermissions) => myPermissions?.SystemLoadCanView ?? false,
);

export const selectCanEditAdminPermissions = createSelector(
  selectMyPermissions,
  (myPermissions) => myPermissions?.AdminPermissionsCanEdit ?? false,
);

export const selectCanViewAdminPermissions = createSelector(
  selectMyPermissions,
  (myPermissions) =>
    (myPermissions?.AdminPermissionsCanView ||
      myPermissions?.AdminPermissionsCanEdit) ??
    false,
);

export const selectCanViewApiKeys = createSelector(
  selectMyPermissions,
  (myPermissions) => myPermissions?.APIKeyCanView ?? false,
);

export const selectCanEditInventoryProducts = createSelector(
  selectMyPermissions,
  (myPermissions) => myPermissions?.BillingCanEditInventory ?? false,
);

export const selectCanEditBillingZones = createSelector(
  selectMyPermissions,
  (myPermissions) => myPermissions?.BillingZoneCanEdit ?? false,
);

export const selectCanViewMFA = createSelector(
  selectMyPermissions,
  (myPermissions) => myPermissions?.MFACanView ?? false,
);

export const selectCanEnforceMFA = createSelector(
  selectMyPermissions,
  (myPermissions) => myPermissions?.MFACanEnforce ?? false,
);

export const selectCanSetupMFA = createSelector(
  selectMyPermissions,
  (myPermissions) => myPermissions?.MFACanSetup ?? false,
);

export const selectCanEditAdminMFA = createSelector(
  selectMyPermissions,
  (myPermissions) => myPermissions?.MFACanEditAdmin ?? false,
);
